/**
 * BondToBondCalculator - BB Arrow Type
 *
 * Handles arrows from bonds to bonds
 * Common in conjugated systems or π-bond shifts
 */

import {
  ArrowCalculator,
  type ArrowCalculationConfig,
  type ArrowEndpoints,
} from "./types";
import {
  defaultBondPositioner,
  type BondPositioner,
} from "../positioning/BondPositioner";
import { blendDirections } from "../core/geometry";
import { ENDPOINT_CONSTANTS } from "../core/constants";

/**
 * Calculator for Bond-to-Bond (BB) arrows
 *
 * Source: Bond
 * Destination: Bond
 */
export class BondToBondCalculator extends ArrowCalculator {
  private bondPositioner: BondPositioner;

  constructor(bondPositioner: BondPositioner = defaultBondPositioner) {
    super();
    this.bondPositioner = bondPositioner;
  }

  getTypeName(): string {
    return "Bond→Bond (BB)";
  }

  calculate(config: ArrowCalculationConfig): ArrowEndpoints {
    const { source, destination, cameraDir } = config;

    // Validate types
    if (source.type !== "bond") {
      throw new Error(
        `BondToBondCalculator expects source type 'bond', got '${source.type}'`
      );
    }
    if (destination.type !== "bond") {
      throw new Error(
        `BondToBondCalculator expects destination type 'bond', got '${destination.type}'`
      );
    }

    let endPos = destination.position;
    const bondPos = source.position;

    // Handle fish arrow (shortens to midpoint if not adjacent)
    const fishResult = this.handleFishArrow(config, bondPos, endPos);
    endPos = fishResult.endPos;

    // Step 1: Calculate start position (choose bond end closer to destination)
    let startPos = this.bondPositioner.calculateStartPoint(source, endPos);

    // Step 2: Calculate start direction (perpendicular to source bond)
    const initialStartDir = this.bondPositioner.calculateDirection(
      source,
      endPos,
      cameraDir
    );

    // Step 3: Calculate end direction (perpendicular to destination bond)
    let endDir = this.bondPositioner.calculateDirection(
      destination,
      startPos,
      cameraDir
    );

    // Step 4: Blend start and end directions for smooth curve
    const startDir = blendDirections(
      initialStartDir,
      endDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Recalculate start position with blended direction (if needed)
    startPos = this.bondPositioner.calculateStartPoint(source, endPos);

    // Re-blend end direction
    endDir = blendDirections(
      endDir,
      initialStartDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Step 5: Calculate final endpoint on destination bond
    const p3 = this.bondPositioner.calculateEndPoint(
      destination,
      startPos,
      endDir,
      cameraDir
    );

    return {
      startPos,
      endPos,
      p3,
      startDir,
      endDir,
      isDestAdjacent: config.isDestAdjacent,
    };
  }
}

/**
 * Default singleton instance
 */
export const defaultBondToBondCalculator = new BondToBondCalculator();
